import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";

import { CustomerAuthProvider, useCustomerAuth } from "@/components/customer/CustomerAuthProvider";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CustomerPortalLayout } from "@/components/customer/CustomerPortalLayout";
import { ErrorBoundary } from "@/components/customer/ErrorBoundary";
import { useRouteTracking } from "@/hooks/useRouteTracking";

import { PortalEntrypoint } from "@/pages/customer/PortalEntrypoint";
import { MagicLinkVerification } from "@/pages/customer/MagicLinkVerification";
import { MagicLinkRequest } from "@/pages/customer/MagicLinkRequest";
import { OfflinePage } from "@/pages/portal/OfflinePage";
import QROnboardingEnhanced from "@/pages/customer/QROnboardingEnhanced";
import PaymentSuccess from "@/pages/customer/PaymentSuccess";
import CustomerAuthPage from "@/pages/customer/CustomerAuthPage";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import CustomerAppointments from "@/pages/customer/CustomerAppointments";
import CustomerOrders from "@/pages/customer/CustomerOrders";
import CustomerWishlist from "@/pages/customer/CustomerWishlist";
import CustomerLoyalty from "@/pages/customer/CustomerLoyalty";
import CustomerPayments from "@/pages/customer/CustomerPayments";
import CustomerHelp from "@/pages/customer/CustomerHelp";
import CustomerHelpArticle from "@/pages/customer/CustomerHelpArticle";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: false, staleTime: 30_000 },
  },
});

type AppProps = { basename: string };

export default function App({ basename }: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <BrowserRouter basename={basename === "/" ? undefined : basename}>
          <LanguageProvider>
            <CustomerAuthProvider>
              <Toaster />
              <RouteTracker />
              <Routes>
                {/* Public */}
                <Route path="/" element={<PortalEntrypoint />} />
                <Route path="/login" element={<MagicLinkRequest />} />
                <Route path="/magic-link" element={<MagicLinkVerification />} />
                <Route path="/offline" element={<OfflinePage />} />
                <Route path="/onboarding" element={<QROnboardingEnhanced />} />
                <Route path="/pay/success" element={<PaymentSuccess />} />

                {/* Authenticated customer routes */}
                <Route path="/dashboard" element={<GuardedPage><CustomerDashboard /></GuardedPage>} />
                <Route path="/appointments" element={<GuardedPage><CustomerAppointments /></GuardedPage>} />
                <Route path="/orders" element={<GuardedPage><CustomerOrders /></GuardedPage>} />
                <Route path="/wishlist" element={<GuardedPage><CustomerWishlist /></GuardedPage>} />
                <Route path="/loyalty" element={<GuardedPage><CustomerLoyalty /></GuardedPage>} />
                <Route path="/payments" element={<GuardedPage><CustomerPayments /></GuardedPage>} />
                <Route path="/help" element={<GuardedPage><CustomerHelp /></GuardedPage>} />
                <Route path="/help/:slug" element={<GuardedPage><CustomerHelpArticle /></GuardedPage>} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </CustomerAuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

function RouteTracker() {
  useRouteTracking();
  return null;
}

function GuardedPage({ children }: { children: React.ReactNode }) {
  const { user, customer, loading } = useCustomerAuth();

  if (!user || !customer) {
    if (loading) return <LoadingSplash />;
    return <CustomerAuthPage />;
  }

  return <CustomerPortalLayout>{children}</CustomerPortalLayout>;
}

function LoadingSplash() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
        <p className="text-sm text-muted-foreground">Loading your bridal portal…</p>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <main className="min-h-screen flex items-center justify-center p-8">
      <p className="text-sm text-muted-foreground">Not found.</p>
    </main>
  );
}
